/**
 * Status of a custom MCP server that authenticates with OAuth (`auth_type:
 * 'mcp_oauth'`), shared by the MCP Servers tab and the Platforms grid so the
 * same server reads the same state on both.
 *
 * The list endpoint adds an `oauth` block to those rows:
 *
 *   - `authorized`             — tokens are in place; the introspection
 *                                `status` is the truth, exactly as for a
 *                                None / Bearer server.
 *   - `authorization_required` — the row was saved but nobody finished the
 *                                sign-in at the provider yet (row `pending`).
 *   - `reconnect_required`     — the token was rejected, revoked or expired
 *                                without a refresh token (row `error`); its
 *                                tools are gone until someone reconnects.
 *
 * Rows without an `oauth` block (None, Bearer, catalog) get `null` from every
 * helper here, so callers keep their existing behaviour for them.
 */
import {
  MCP_STATUS_FILTER_ALL,
  McpConnectionStatusView,
} from './mcp-connection-status';

/** The subset of an mcp-connections row these decisions need. */
export interface CustomOauthRow {
  type?: string | null;
  status?: string | null;
  oauth?: { status?: string | null } | null;
}

export type CustomOauthState =
  | 'authorized'
  | 'authorization_required'
  | 'reconnect_required';

const KNOWN_STATES: CustomOauthState[] = [
  'authorized',
  'authorization_required',
  'reconnect_required',
];

/**
 * The OAuth state of a custom row, or `null` when the row is not an OAuth
 * custom server (or carries a state this client does not know).
 */
export const getCustomOauthState = (
  conn: CustomOauthRow | null | undefined
): CustomOauthState | null => {
  if (!conn || conn.type !== 'custom' || !conn.oauth) return null;
  const state = conn.oauth.status;
  return KNOWN_STATES.includes(state as CustomOauthState)
    ? (state as CustomOauthState)
    : null;
};

/**
 * The pill for an OAuth custom row that is not usable yet, in the Platforms
 * tab vocabulary (`labelKey` + `color`) — or `null` when the normal pill
 * applies (authorized, or not an OAuth row).
 */
export const getCustomOauthPillView = (
  conn: CustomOauthRow | null | undefined
): McpConnectionStatusView | null => {
  switch (getCustomOauthState(conn)) {
    case 'authorization_required':
      return {
        status: 'setup_incomplete',
        labelKey: 'ControlCenterOauthAuthorizationRequired',
        color: 'yellow',
      };
    case 'reconnect_required':
      return {
        status: 'reconnect_needed',
        labelKey: 'MCP-ReconnectNeeded',
        color: 'red',
      };
    default:
      return null;
  }
};

/**
 * Which authorize action a card offers: `connect` for a sign-in that never
 * finished, `reconnect` for a dead token, `null` otherwise. Callers still gate
 * it on `integrations.manage`.
 */
export const getCustomOauthAction = (
  conn: CustomOauthRow | null | undefined
): 'connect' | 'reconnect' | null => {
  switch (getCustomOauthState(conn)) {
    case 'authorization_required':
      return 'connect';
    case 'reconnect_required':
      return 'reconnect';
    default:
      return null;
  }
};

/**
 * The Platforms-grid status filter for a custom server card. Non-OAuth rows
 * keep the existing rule (connected → Connected, error → Reconnect Needed);
 * OAuth rows that are not authorized file under the state their pill shows.
 */
export const matchesCustomServerFilter = (
  conn: CustomOauthRow | null | undefined,
  filter: string
): boolean => {
  if (filter === MCP_STATUS_FILTER_ALL) return true;
  if (!conn) return false;
  const pill = getCustomOauthPillView(conn);
  switch (filter) {
    case 'Connected':
      return !pill && conn.status === 'connected';
    case 'Reconnect Needed':
      return pill
        ? pill.status === 'reconnect_needed'
        : conn.status === 'error';
    case 'Finish Setup':
      return pill?.status === 'setup_incomplete';
    default:
      return false;
  }
};
